import { z } from 'zod/v4';
import { createDefinitionSchema } from '../utils/helpers';

const optionalLabelValue = z.object({
  label: z.string(),
  value: z.string(),
});

export const staticListVariableDefinitionSchema = createDefinitionSchema(
  'StaticListVariable',
  z.object({
    values: z.array(z.union([z.string(), optionalLabelValue])),
  })
);

/**
 * Operator types for variable injection in query matchers.
 * Used when injecting variables as label matchers in Prometheus queries.
 */
export type InjectVariableOperator = '=' | '=~' | '!=' | '!~';

/**
 * Type of injection to perform when injecting a variable into a query.
 * - 'LabelMatcher': Injects as a label matcher, e.g., {instance=~"$var"}
 * - 'RangeVector': Injects into range vector duration, e.g., [5m] -> [$var]
 */
export type VariableInjectionType = 'LabelMatcher' | 'RangeVector';

/**
 * Base options shared by all injection types.
 */
interface BaseInjectionOptions {
  /**
   * The variable name (without $ prefix)
   */
  name: string;
}

/**
 * Options for injecting a variable as a label matcher.
 * Example: `up` becomes `up{instance=~"$instance"}`
 */
export interface LabelMatcherInjection extends BaseInjectionOptions {
  injectionType: 'LabelMatcher';
  /**
   * The label name to use in the matcher.
   */
  labelName: string;
  /**
   * The operator to use for matching (defaults to =~).
   */
  operator?: InjectVariableOperator;
}

/**
 * Options for injecting a variable into range vector duration.
 * Example: `rate(metric[5m])` becomes `rate(metric[$interval])`
 */
export interface RangeVectorInjection extends BaseInjectionOptions {
  injectionType: 'RangeVector';
}

/**
 * Variable injection options.
 * Use `injectionType` to determine which fields are available.
 */
export type InjectVariableOptions = LabelMatcherInjection | RangeVectorInjection;

/** Use =~ operator to support both single and multi-value variables */
export const DEFAULT_INJECT_OPERATOR: InjectVariableOperator = '=~';
